import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { get, isUndefined, partition, pull } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map, pairwise, switchMap } from 'rxjs/operators';
import { isPointCloudOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { assertDefined } from '../../../../../cross-team-shared/misc-utils/strict-record';
import {
  getNumericSharedCssProperty,
  getSharedCssProperty,
  setSharedCssProperties,
} from '../../../../../outputs/annotation/annotation-shared/properties-shared-with-css';
import { Output } from '../../../../../team-apis/otterdam/outputs/output';
import { PointCloudOutput } from '../../../../../team-apis/otterdam/outputs/point-cloud-output';
import { FusionManagerFactory } from '../../../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { AnnotationLayoutService } from '../../../../outputs/annotation/annotation-shared/layout/annotation-layout.service';
import { RootKeyboardShortcutService } from '../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { FeatureFlagService } from '../../../../shared/misc-services/feature-flag.service';
import { ViewContainerMappingService } from '../../../../shared/view-container-mapping/view-container-mapping.service';
import { TaskWorkspaceStore } from '../../../state/task-workspace-store';
import { SplitViewService } from './split-view.service';

@Component({
  selector: 'h3-split-view',
  templateUrl: './split-view.component.html',
  styleUrl: './split-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SplitViewComponent
  extends DirectiveSuperclass
  implements OnInit, OnDestroy
{
  @Input() outputs: Output[] = [];
  private readonly viewContainerMappingService = inject(
    ViewContainerMappingService,
  );
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly fusionManagerFactory = inject(FusionManagerFactory);
  private readonly splitViewService = inject(SplitViewService);
  private readonly featureFlagService = inject(FeatureFlagService);

  leftContainerId = 'split-view-left';
  rightContainerId = 'split-view-right';
  splitViewOutputInjector$ = this.taskWorkspaceStore(
    'splitViewOutputName',
  ).$.pipe(
    map((splitViewOutputName) => {
      if (!splitViewOutputName) {
        return undefined;
      }
      const { project } = this.taskWorkspaceStore.state();
      const splitViewOutput = project.outputs.find(
        (output) => output.name === splitViewOutputName,
      );
      assert(splitViewOutput, 'splitViewOutput should always be defined');

      const minion = this.fusionManagerFactory
        .getManager(splitViewOutput.id)
        .getMinion(splitViewOutput.id);
      assert(minion, 'minion should always be defined');
      return minion.injector;
    }),
  );

  sidePanelHeight$ = this.splitViewOutputInjector$.pipe(
    filter(isDefined),
    switchMap((injector) =>
      injector.get(AnnotationLayoutService).getAnnotationHeight$(),
    ),
    map(
      (annotationHeight) =>
        `calc(${annotationHeight} + ${getSharedCssProperty('split-view-toolbar-height')})`,
    ),
  );
  objectListWidth = `${getNumericSharedCssProperty('object-list-width')}px`;
  // sensor fusion project should always have a point cloud output
  pointCloudOutput!: PointCloudOutput;
  splitViewOutputName$ = this.taskWorkspaceStore('splitViewOutputName').$;
  selectedOutputName$ = this.taskWorkspaceStore('selectedOutputName').$;
  isSingleFrame$!: Observable<boolean>;
  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );
  isLeftOutputSelected$ = this.selectedOutputName$.pipe(
    map((outputName) => outputName === this.pointCloudOutput.name),
  );
  isUsingEvenSplit$ = this.taskWorkspaceStore('settings')('useEvenSplit').$;
  outputTabsEnabled = this.featureFlagService.hasFlag('output_tabs');
  leftOutputs: Output[];
  rightOutputs: Output[];

  private shortcutService = inject(RootKeyboardShortcutService);

  constructor(injector: Injector) {
    super(injector);
    setSharedCssProperties(inject(ElementRef));
    const [leftOutputs, rightOutputs] = partition(
      this.taskWorkspaceStore.state().outputsByDisplayOrder,
      isPointCloudOutput,
    );
    this.leftOutputs = leftOutputs;
    this.rightOutputs = rightOutputs;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const output = get(this.outputs, +event.key - 1, undefined);

    if (
      this.shouldIgnoreKeyboardEvent(event) ||
      this.shouldPreventChangingSplitViewOutput(output)
    ) {
      return;
    }
    assertDefined(output);

    this.splitViewService.changeSplitViewOutput(
      output.name,
      'keyboard_shortcut',
      this.taskWorkspaceStore.state().selectedOutputName !==
        this.pointCloudOutput.name,
    );
  }

  ngOnDestroy() {
    this.taskWorkspaceStore('splitViewOutputName').set(undefined);
  }

  ngOnInit() {
    const { project } = this.taskWorkspaceStore.state();
    this.pointCloudOutput = project.outputs.filter(isPointCloudOutput)[0];
    this.isSingleFrame$ = this.fusionManagerFactory.getManager(
      this.pointCloudOutput.id,
    ).areAllSingleFrame$;

    this.setUpSubscriptionToSplitViewOutputName();
    this.setUpSubscriptionToToggleFocusedSplitViewShortcut();
  }

  protected selectPointCloudOutput(event: Event) {
    event.stopPropagation();
    this.splitViewService.changeActiveOutput(
      this.pointCloudOutput.name,
      'click_sv',
    );
  }

  protected selectVideoOutput(event?: Event) {
    event?.stopPropagation();
    const { splitViewOutputName } = this.taskWorkspaceStore.state();
    assert(splitViewOutputName, 'splitViewOutputName should always be defined');

    this.splitViewService.changeActiveOutput(splitViewOutputName, 'click_sv');
  }

  private setUpSubscriptionToSplitViewOutputName() {
    this.subscribeTo(
      this.splitViewOutputName$.pipe(
        pairwise(),
        filter(([_, splitViewOutputName]) => !!splitViewOutputName),
      ),
      ([previousSplitViewOutput, splitViewOutputName]) => {
        if (previousSplitViewOutput) {
          this.viewContainerMappingService.switchOutputViewToThumbnail(
            previousSplitViewOutput,
          );
        }
        if (splitViewOutputName) {
          this.viewContainerMappingService.switchOutputViewToSplitView(
            this.pointCloudOutput.name,
            this.leftContainerId,
          );
          this.viewContainerMappingService.switchOutputViewToSplitView(
            splitViewOutputName,
            this.rightContainerId,
          );
        }
      },
    );
  }

  private toggleSelectedOutput(source: string) {
    const isUpsertFeedbackDialogOpen =
      this.taskWorkspaceStore.state().feedbackPopover.target === 'upsert';

    if (isUpsertFeedbackDialogOpen) {
      return;
    }

    const { splitViewOutputName, selectedOutputName } =
      this.taskWorkspaceStore.state();
    assert(splitViewOutputName, 'splitViewOutputName should always be defined');

    const splitViewOutputs: [string, string] = [
      this.pointCloudOutput.name,
      splitViewOutputName,
    ];
    pull(splitViewOutputs, selectedOutputName);

    this.splitViewService.changeActiveOutput(splitViewOutputs[0], source);
  }

  private setUpSubscriptionToToggleFocusedSplitViewShortcut() {
    this.subscribeTo(
      this.shortcutService
        .getEvent$('cycleSelectedOutputInSplitView')
        .pipe(
          filter(() => !!this.taskWorkspaceStore.state().splitViewOutputName),
        ),
      () => {
        this.toggleSelectedOutput('keyboard_shortcut');
      },
    );
  }

  private shouldIgnoreKeyboardEvent($event: KeyboardEvent) {
    return (
      $event.target instanceof HTMLInputElement ||
      $event.target instanceof HTMLTextAreaElement ||
      isNaN(+$event.key)
    );
  }

  private shouldPreventChangingSplitViewOutput(output: Output | undefined) {
    const isUpsertFeedbackDialogOpen =
      this.taskWorkspaceStore.state().feedbackPopover.target === 'upsert';

    const isSplitViewActive = isDefined(
      this.taskWorkspaceStore.state().splitViewOutputName,
    );

    return (
      isUpsertFeedbackDialogOpen ||
      !isSplitViewActive ||
      isUndefined(output) ||
      output.name === this.pointCloudOutput.name
    );
  }
}
